/**
 * Productivity Dashboard — Mock HTTP Interceptor
 *
 * Intercepts all requests to the productivity dashboard API and returns stub data.
 * Registered in app.module.ts behind environment.useMockApi.
 *
 * DELETE THIS FILE when the real API is live — no other code changes required.
 */

import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  ProductivityBaselineItemDto,
  ProductivityHoursDto,
  ProductivityProgressDto,
  ProductivityProjectConfigDto,
  ProductivityProjectDto,
} from '@app/core/models/productivity-dashboard.model';

const MATCH_PATH = 'dashboards/productivity';

// ── Stub data ────────────────────────────────────────────────────────────────

const STUB_PROJECTS: ProductivityProjectDto[] = [
  { projectId: 1, projectName: 'Riverside Bridge Upgrade', projectCode: 'PRJ-001', description: 'Major infrastructure upgrade on Riverside Bridge' },
  { projectId: 2, projectName: 'Northgate Industrial Park', projectCode: 'PRJ-002', description: null },
];

const STUB_CONFIG: ProductivityProjectConfigDto = {
  projectId: 1,
  baselineId: 101,
  isLevel1Active: true,
  isSubAreaActive: false,
  isLevel2Active: true,
  isLevel3Active: true,
  level1Label: 'Level',
  subAreaLabel: 'Sub Area',
  level2Label: 'Cost Code',
  level3Label: 'Element',
  depth: 3,
};

const STUB_BASELINE: ProductivityBaselineItemDto[] = [
  {
    baselineItemId: 1,
    level1Id: 10, level1Name: 'Zone A', level1SortOrder: 1,
    subAreaId: null, subAreaName: null, subAreaRef: null, subAreaSortOrder: 0,
    level2Id: 20, level2Name: 'C001', level2Ref: 'Concrete Works', level2SortOrder: 1,
    level3Id: 30, level3Name: 'Formwork', level3Ref: 'FW', level3SortOrder: 1,
    estimatedQuantity: 500, estimatedQuantityUnit: 'meter-squared',
    estimatedHours: 200, productivityCalcType: 'unit-per-hour',
    estimatedProductivityRate: 2.5,
    allowWork: true, previousAllowWork: null,
    progressInputMode: 'percent',
  },
  {
    baselineItemId: 2,
    level1Id: 10, level1Name: 'Zone A', level1SortOrder: 1,
    subAreaId: null, subAreaName: null, subAreaRef: null, subAreaSortOrder: 0,
    level2Id: 20, level2Name: 'C001', level2Ref: 'Concrete Works', level2SortOrder: 1,
    level3Id: 31, level3Name: 'Reo Steel', level3Ref: 'RS', level3SortOrder: 2,
    estimatedQuantity: 80, estimatedQuantityUnit: 'each',
    estimatedHours: 160, productivityCalcType: 'hour-per-unit',
    estimatedProductivityRate: 2.0,
    allowWork: true, previousAllowWork: null,
    progressInputMode: 'quantity',
  },
  {
    baselineItemId: 3,
    level1Id: 11, level1Name: 'Zone B', level1SortOrder: 2,
    subAreaId: null, subAreaName: null, subAreaRef: null, subAreaSortOrder: 0,
    level2Id: 21, level2Name: 'C002', level2Ref: 'Earthworks', level2SortOrder: 2,
    level3Id: 32, level3Name: 'Excavation', level3Ref: 'EX', level3SortOrder: 1,
    estimatedQuantity: 1200, estimatedQuantityUnit: 'meter-cubed',
    estimatedHours: 300, productivityCalcType: 'unit-per-hour',
    estimatedProductivityRate: 4.0,
    allowWork: true, previousAllowWork: null,
    progressInputMode: 'percent',
  },
];

// Hours fan-out: staff-01 has 2 skill tags → rows are duplicated with same hours
const STUB_HOURS: ProductivityHoursDto[] = [
  // Zone A / C001 / Formwork — staff-01 with skill tag 1
  { date: '2026-02-01T00:00:00', level1Id: 10, level1Name: 'Zone A', subAreaId: null, subAreaName: null, level2Id: 20, level2Name: 'C001', level3Id: 30, level3Name: 'Formwork', hours: 8, staffId: 'staff-01', skillTagId: 1, skillTag: 'Carpenter' },
  // Zone A / C001 / Formwork — staff-01 with skill tag 2 (FAN-OUT — same hours, different skill tag)
  { date: '2026-02-01T00:00:00', level1Id: 10, level1Name: 'Zone A', subAreaId: null, subAreaName: null, level2Id: 20, level2Name: 'C001', level3Id: 30, level3Name: 'Formwork', hours: 8, staffId: 'staff-01', skillTagId: 2, skillTag: 'Formworker' },
  // Zone A / C001 / Formwork — staff-02 with single skill tag
  { date: '2026-02-01T00:00:00', level1Id: 10, level1Name: 'Zone A', subAreaId: null, subAreaName: null, level2Id: 20, level2Name: 'C001', level3Id: 30, level3Name: 'Formwork', hours: 7.5, staffId: 'staff-02', skillTagId: 1, skillTag: 'Carpenter' },
  // Zone A / C001 / Reo Steel — staff-03
  { date: '2026-02-02T00:00:00', level1Id: 10, level1Name: 'Zone A', subAreaId: null, subAreaName: null, level2Id: 20, level2Name: 'C001', level3Id: 31, level3Name: 'Reo Steel', hours: 10, staffId: 'staff-03', skillTagId: 3, skillTag: 'Steel Fixer' },
  // Zone B / C002 / Excavation — staff-04
  { date: '2026-02-03T00:00:00', level1Id: 11, level1Name: 'Zone B', subAreaId: null, subAreaName: null, level2Id: 21, level2Name: 'C002', level3Id: 32, level3Name: 'Excavation', hours: 12, staffId: 'staff-04', skillTagId: null, skillTag: null },
  { date: '2026-02-04T00:00:00', level1Id: 11, level1Name: 'Zone B', subAreaId: null, subAreaName: null, level2Id: 21, level2Name: 'C002', level3Id: 32, level3Name: 'Excavation', hours: 9, staffId: 'staff-04', skillTagId: null, skillTag: null },
];

const STUB_PROGRESS: ProductivityProgressDto[] = [
  { level1Id: 10, level1Name: 'Zone A', subAreaId: null, subAreaName: null, level2Id: 20, level2Name: 'C001', level3Id: 30, level3Name: 'Formwork', percentageComplete: 60, actualQty: 300, timestamp: '2026-02-28T00:00:00' },
  { level1Id: 10, level1Name: 'Zone A', subAreaId: null, subAreaName: null, level2Id: 20, level2Name: 'C001', level3Id: 31, level3Name: 'Reo Steel', percentageComplete: 40, actualQty: 32, timestamp: '2026-02-28T00:00:00' },
  { level1Id: 11, level1Name: 'Zone B', subAreaId: null, subAreaName: null, level2Id: 21, level2Name: 'C002', level3Id: 32, level3Name: 'Excavation', percentageComplete: 75, actualQty: 900, timestamp: '2026-02-28T00:00:00' },
];

// ── Interceptor ───────────────────────────────────────────────────────────────

@Injectable()
export class ProductivityMockInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.includes(MATCH_PATH)) {
      return next.handle(req);
    }

    const url = req.url;

    // GET /projects (no project ID segment after 'projects')
    if (/dashboards\/productivity\/projects$/.test(url)) {
      return of(new HttpResponse({ status: 200, body: STUB_PROJECTS }));
    }

    // GET /projects/{id}/config
    if (/projects\/\d+\/config$/.test(url)) {
      return of(new HttpResponse({ status: 200, body: STUB_CONFIG }));
    }

    // GET /projects/{id}/baseline
    if (/projects\/\d+\/baseline$/.test(url)) {
      return of(new HttpResponse({ status: 200, body: STUB_BASELINE }));
    }

    // GET /projects/{id}/hours
    if (/projects\/\d+\/hours/.test(url)) {
      return of(new HttpResponse({ status: 200, body: STUB_HOURS }));
    }

    // GET /projects/{id}/progress
    if (/projects\/\d+\/progress$/.test(url)) {
      return of(new HttpResponse({ status: 200, body: STUB_PROGRESS }));
    }

    return next.handle(req);
  }
}
